import { getAuth } from 'firebase/auth';

class VersionHistoryService {
    constructor() {
        this.API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';
    }

    /**
     * Get authentication token
     */
    async getAuthToken() {
        const auth = getAuth();
        if (auth.currentUser) {
            return await auth.currentUser.getIdToken();
        }
        return null;
    }

    /**
     * Save a version snapshot
     * @param {string} episodeId - Episode ID
     * @param {string} content - Markdown content
     * @param {string} description - Optional description for this version
     * @returns {Promise<Object>} Save result
     */
    async saveVersion(episodeId, content, description = '') {
        try {
            const token = await this.getAuthToken();
            if (!token) {
                throw new Error('Authentication required');
            }

            const response = await fetch(`${this.API_BASE_URL}/api/vault/versions`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    episodeId,
                    content,
                    description
                })
            });

            const result = await response.json();

            if (!response.ok) {
                throw new Error(result.error || 'Failed to save version');
            }

            return result;

        } catch (error) {
            throw error;
        }
    }

    /**
     * List versions for an episode
     * @param {string} episodeId - Episode ID
     * @param {number} limit - Max number of versions to return
     * @returns {Promise<Object>} List result
     */
    async listVersions(episodeId, limit = 20) {
        try {
            const token = await this.getAuthToken();
            if (!token) {
                throw new Error('Authentication required');
            }

            const response = await fetch(
                `${this.API_BASE_URL}/api/vault/versions?episodeId=${episodeId}&limit=${limit}`,
                {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                }
            );

            const result = await response.json();

            if (!response.ok) {
                throw new Error(result.error || 'Failed to list versions');
            }

            return result;

        } catch (error) {
            throw error;
        }
    }

    /**
     * Get a specific version
     * @param {string} versionId - Version ID
     * @returns {Promise<Object>} Version data
     */
    async getVersion(versionId) {
        try {
            const token = await this.getAuthToken();
            if (!token) {
                throw new Error('Authentication required');
            }

            const response = await fetch(
                `${this.API_BASE_URL}/api/vault/versions/${versionId}`,
                {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                }
            );

            const result = await response.json();

            if (!response.ok) {
                throw new Error(result.error || 'Failed to get version');
            }

            return result;

        } catch (error) {
            throw error;
        }
    }

    /**
     * Delete a version
     * @param {string} versionId - Version ID
     * @returns {Promise<Object>} Delete result
     */
    async deleteVersion(versionId) {
        try {
            const token = await this.getAuthToken();
            if (!token) {
                throw new Error('Authentication required');
            }

            const response = await fetch(
                `${this.API_BASE_URL}/api/vault/versions/${versionId}`,
                {
                    method: 'DELETE',
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                }
            );

            const result = await response.json();

            if (!response.ok) {
                throw new Error(result.error || 'Failed to delete version');
            }

            return result;

        } catch (error) {
            throw error;
        }
    }

    /**
     * Bulk delete all versions for an episode
     * @param {string} episodeId - Episode ID
     * @returns {Promise<Object>} Bulk delete result
     */
    async bulkDeleteVersions(episodeId) {
        try {
            const token = await this.getAuthToken();
            if (!token) {
                throw new Error('Authentication required');
            }

            const response = await fetch(
                `${this.API_BASE_URL}/api/vault/versions/bulk/all`,
                {
                    method: 'DELETE',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({ episodeId })
                }
            );

            const result = await response.json();

            if (!response.ok) {
                throw new Error(result.error || 'Failed to bulk delete versions');
            }

            return result;

        } catch (error) {
            throw error;
        }
    }

    /**
     * Format relative time for display
     * @param {Object} timestamp - Firestore timestamp
     * @returns {string} Relative time string
     */
    formatRelativeTime(timestamp) {
        if (!timestamp) return 'Unknown';

        let date;
        if (timestamp && typeof timestamp.toDate === 'function') {
            date = timestamp.toDate();
        } else if (timestamp && typeof timestamp._seconds === 'number') {
            date = new Date(timestamp._seconds * 1000);
        } else if (typeof timestamp === 'string' || timestamp instanceof Date) {
            date = new Date(timestamp);
        } else {
            return 'Unknown';
        }

        if (isNaN(date.getTime())) return 'Unknown';

        const now = new Date();
        const diffMs = now - date;
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        if (diffMins < 1) return 'Just now';
        if (diffMins < 60) return `${diffMins}m ago`;
        if (diffHours < 24) return `${diffHours}h ago`;
        if (diffDays < 7) return `${diffDays}d ago`;

        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
}

export default new VersionHistoryService();
